import { resolveFieldLabel } from "@repo/shared";
import type { FinancialEntryItem, FinancialFieldDefinition } from "@repo/shared/types";
import { useMemo } from "react";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./commitments-tab.i18n";
import { EntryList } from "./entry-list";
import { TypeSummary } from "./type-summary";

type CommitmentsTabProps = {
  entries: FinancialEntryItem[];
  fieldDefs: FinancialFieldDefinition[];
  /** QL-39 — see EntryList. */
  requiredTypeIds?: ReadonlySet<string>;
  missingRequiredIds?: ReadonlySet<string>;
  disabled: boolean;
  currency: string;
  lang: string;
  totalMonthlyCommitments: number;
  onAdd: () => void;
  onRemove: (id: string) => void;
  onUpdate: (id: string, updates: Partial<FinancialEntryItem>) => void;
};

export function CommitmentsTab({
  entries,
  fieldDefs,
  requiredTypeIds,
  missingRequiredIds,
  disabled,
  currency,
  lang,
  totalMonthlyCommitments,
  onAdd,
  onRemove,
  onUpdate,
}: CommitmentsTabProps) {
  const t = useT(T);

  // QL-586 — the model's current label, not the one stored when the row was created.
  const labelFor = (entry: FinancialEntryItem) => {
    const def = fieldDefs.find((d) => d.id === entry.typeId);
    return (def ? resolveFieldLabel(def, lang) : "") || entry.typeLabel;
  };

  const summaryByType = useMemo(() => {
    const grouped: Record<string, { label: string; total: number }> = {};
    for (const entry of entries) {
      if (!grouped[entry.typeId]) {
        grouped[entry.typeId] = { label: labelFor(entry), total: 0 };
      }
      grouped[entry.typeId].total += entry.amount || 0;
    }
    return grouped;
  }, [entries, fieldDefs, lang]);

  return (
    <div className="space-y-4">
      <EntryList
        entries={entries}
        fieldDefs={fieldDefs}
        requiredTypeIds={requiredTypeIds}
        missingRequiredIds={missingRequiredIds}
        fieldLabels={t.fieldLabels}
        disabled={disabled}
        lang={lang}
        addButtonLabel={t.addCommitment}
        emptyMessage={t.noItemsYet}
        onAdd={onAdd}
        onRemove={onRemove}
        onUpdate={onUpdate}
      />

      <TypeSummary
        summary={summaryByType}
        total={totalMonthlyCommitments}
        currency={currency}
        lang={lang}
      />
    </div>
  );
}
